<script setup>

import {onMounted, ref} from 'vue';
import DocumentService from "@/services/DocumentService";
import Button from "@/components/forms/Button.vue";
import {formatDate, showSuccessMessage} from '@/helper';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import SignModal from '@/components/modals/SignModal.vue';
import {authData, refreshLoggedUser} from '@/services/AuthService';
import ModalDialog from '@/components/modals/ModalDialog.vue';
import DownloadButtonComponent from '@/components/documents/DownloadButtonComponent.vue';
import {useI18n} from 'vue-i18n'

const {t} = useI18n()

const props = defineProps({
  entityId: {
    type: Number,
    default: null,
  },
  archive: {
    type: Boolean,
    default: false,
  },
});

const documents = ref([]);
const checkedDocuments = ref([]);
const isLoading = ref(true);
const showCheckbox = window.location.href.includes('my-documents') && !props.archive;


onMounted(async () => {
  getDocuments();
  emitter.on('documentGenerated', () => {
    getDocuments();
  });
});

function getSignatureStateLabel(state) {
  switch (state) {
    case 'PENDING':
      return t('documentListSimpleComponent.pending');
    case 'SIGNED':
      return t('documentListSimpleComponent.signed');
    case 'REJECTED':
      return t('documentListSimpleComponent.rejected');
    default:
      return state;
  }
}

function getDocuments() {
  isLoading.value = true;
  if (!props.entityId) {
    DocumentService.getMyDocuments()
        .then((response) => {
          if (props.archive) {
            documents.value = response.filter((document) => document.signature_current_user?.state !== 'PENDING');
          } else {
            documents.value = response.filter((document) => document.signature_current_user?.state === 'PENDING');
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          isLoading.value = false;
        });
  } else {
    DocumentService.getDocumentsForClient(props.entityId)
        .then((response) => {
          documents.value = response;
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          isLoading.value = false;
        });
  }
}

function openSignModal() {
  const {reveal, onConfirm} = createConfirmDialog(SignModal, {documentsIds: checkedDocuments.value});
  reveal();
  onConfirm(() => {
    showSuccessMessage(t('documentListSimpleComponent.documents_successfully_signed'));
    getDocuments();
    refreshLoggedUser();
    checkedDocuments.value = [];
  });
}

function onCheck(documentId) {
  if (checkedDocuments.value.includes(documentId)) {
    checkedDocuments.value = checkedDocuments.value.filter((id) => id !== documentId);
  } else {
    checkedDocuments.value.push(documentId);
  }
}

function deleteDocument(documentId) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('documentListSimpleComponent.document_delete'),
    message: t('documentListSimpleComponent.document_delete_really'),
  });
  onConfirm(() => {
    DocumentService.deleteDocument(documentId)
        .then(() => {
          showSuccessMessage(t('documentListSimpleComponent.document_delete_confirmed'));
          getDocuments();
        })
        .catch((error) => {
          console.log(error);
        });
  });
  reveal();
}

</script>

<template>
  <div>

    <div class="table-container">
      <table class="table table--last-to-right table--cells-py-0">
        <thead>
        <tr>
          <th v-if="showCheckbox" scope="col">

          </th>
          <th scope="col">
            {{ t('documentListSimpleComponent.name') }}
          </th>
          <th scope="col">
            {{ t('documentListSimpleComponent.date') }}
          </th>
          <th scope="col">
            {{ t('documentListSimpleComponent.client_signature_required') }}
          </th>
          <th scope="col">
            {{ t('documentListSimpleComponent.action') }}
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="document of documents" :key="document">
          <td v-if="showCheckbox">
            <input :disabled="document.signature_current_user?.state !== 'PENDING'" type="checkbox" :checked="checkedDocuments.includes(document.id)" @change="onCheck(document.id)">
          </td>
          <td>
            {{ document.name }}
          </td>
          <td>
            {{ formatDate(document.created_at, 'd.m.Y H:i') }}
          </td>
          <td>

            {{ document.signature_client ? t('documentListSimpleComponent.yes') : t('documentListSimpleComponent.no') }} ({{ getSignatureStateLabel(document.signature_client?.state) }})
          </td>
          <td>
            <router-link :to="['ADMIN', 'SUPERADMIN', 'BACK_OFFICE'].includes(authData.loggedUser.role) ? { name: 'DocumentDetail', params: { id: document.id} } : { name: 'DocumentDetailSimple', params: { id: document.id} }">
              <button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button>
            </router-link>
            <DownloadButtonComponent v-if="document.file" :uuid="document.file.uuid" :downloadUrl="document.file.download_url"/>
            <!-- SUPERADMIN has same delete permissions as ADMIN -->
            <button v-if="['ADMIN', 'SUPERADMIN', 'BACK_OFFICE'].includes(authData.loggedUser.role)" type="button" class="btn btn-error" @click="deleteDocument(document.id)"><i class="mdi mdi-trash-can-outline"/></button>
          </td>
        </tr>
        <tr v-if="isLoading">
          <td colspan="5" class="text-center">
            <div class="spinner-border text-gray" role="status">
              <span class="visually-hidden">{{ t('documentListSimpleComponent.loading') }}</span>
            </div>
          </td>
        </tr>
        <tr v-else-if="!documents.length">
          <td colspan="5" class="text-center">
            {{ t('documentListSimpleComponent.no_documents_yet') }}
          </td>
        </tr>
        </tbody>
      </table>
      <div v-if="checkedDocuments.length > 0" class="row">
        <div class="col-12 d-flex justify-content-end">
          <Button :label="t('documentListSimpleComponent.bulk_sign')" @click="openSignModal"/>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
